import { Timestamp } from 'firebase/firestore';

// Enhanced organization types extending the existing system
export type EnhancedOrgType =
  | 'evenementiel' // Existing - Events & Entertainment
  | 'restaurant' // Existing - Restaurant & Food Service
  | 'recording_studio' // New - Music/Recording Studios (from EasyBooking)
  | 'service' // New - Professional Services
  | 'commerce' // New - Retail & Commerce
  | 'healthcare' // New - Healthcare & Medical
  | 'education' // New - Education & Training
  | 'fitness' // New - Fitness & Sports
  | 'beauty' // New - Beauty & Wellness
  | 'automotive' // New - Automotive Services
  | 'professional'; // New - Professional Services (Law, Architecture, etc.)

// Enhanced organization interface extending the existing Organisation type
export interface EnhancedOrganisation {
  id?: string;
  name: string;
  description: string;
  bio?: string; // Legacy compatibility
  type: EnhancedOrgType;
  ownerId: string;
  ownerid?: string; // Legacy compatibility
  language?: 'en' | 'fr';
  logo?: string;
  logoImage?: string; // Legacy compatibility
  coverImage?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  contact?: OrganisationContact;
  settings?: OrganisationSettings;
  staffRoles?: OrganisationStaffRole[];
  musicShowcase?: MusicShowcase; // Music showcase for recording studios
  followers?: number; // Legacy compatibility
  totalEvents?: number; // Legacy compatibility

  // Recording studio specific fields
  servicesOffered?: string[]; // Music studio services offered (e.g., 'recording', 'mixing', 'mastering')
  musicStyles?: string[]; // Music genres/styles the studio specializes in

  // Business settings
  isPubliclyVisible?: boolean; // Whether organization appears in public listings
  isBookingOpen?: boolean; // Whether booking is currently accepting new bookings

  legalInfos?: string; // Legacy compatibility
  siren?: string; // Legacy compatibility
  siret?: string; // Legacy compatibility
  companyNumber?: number; // Legacy compatibility
  groupeTable?: any[]; // Legacy compatibility
  isDeleted?: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export interface OrganisationContact {
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  youtube?: string;
  tiktok?: string;
}

export interface OrganisationSettings {
  // Core features enabled for this organization type
  enabledFeatures?: OrganisationFeature[];

  // Business operation settings
  businessHours?: WorkingHours;
  bookingRules?: BookingRules;
  paymentSettings?: PaymentSettings;
  customFields?: CustomField[];

  // Type-specific settings
  eventSettings?: EventSettings; // For evenementiel type
  restaurantSettings?: RestaurantSettings; // For restaurant type
  serviceSettings?: ServiceSettings; // For service types

  // UI and display settings
  theme?: ThemeSettings;
  notifications?: NotificationSettings;
}

export type OrganisationFeature =
  // Core features
  | 'basic'
  | 'staff'
  | 'calendar'
  | 'analytics'
  | 'reports'

  // Event features
  | 'events'
  | 'tickets'
  | 'reservations'
  | 'promotions'
  | 'seating'

  // Restaurant features
  | 'tables'
  | 'menus'
  | 'orders'
  | 'inventory'
  | 'pos'

  // Service features
  | 'appointments'
  | 'services'
  | 'clients'
  | 'invoicing'

  // Healthcare features
  | 'patients'
  | 'medical_records'
  | 'prescriptions'

  // Education features
  | 'students'
  | 'courses'
  | 'curriculum'
  | 'grades'

  // Retail features
  | 'products'
  | 'sales'
  | 'customers'

  // Beauty/Wellness features
  | 'treatments'
  | 'packages'

  // Fitness features
  | 'classes'
  | 'memberships'
  | 'equipment'

  // Automotive features
  | 'vehicles'
  | 'maintenance'
  | 'parts'

  // Professional features
  | 'projects'
  | 'cases'
  | 'billing'

  // Recording studio features (from EasyBooking)
  | 'rooms'
  | 'engineers'
  | 'recording'
  | 'mixing'
  | 'mastering'
  | 'equipment_rental'
  | 'supported_services';

export interface WorkingHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
  // Cross-day booking policy
  crossDayBookingPolicy?: CrossDayPolicy;
}

export interface CrossDayPolicy {
  enabled: boolean;
  maxCrossDayHours: number; // Maximum hours a booking can span across days
  requireSameDayStart: boolean; // Must start and end on same calendar day
  allowWeekendCrossover: boolean; // Can cross from weekend to weekday
}

export interface DaySchedule {
  isWorking: boolean;
  startTime?: string; // "09:00" format
  endTime?: string; // "17:00" format or "02:00" (next day)
  is24Hours?: boolean; // True for 24-hour operation
  crossesMidnight?: boolean; // True if endTime < startTime (e.g., 23:00 to 02:00)
  breaks?: TimeBreak[];
}

export interface TimeBreak {
  id: string;
  name: string;
  startTime: string; // "12:00" format
  endTime: string; // "13:00" format
}

// Enhanced time slot for cross-day bookings
export interface TimeSlot {
  startDate: string; // ISO date string "2024-01-15"
  startTime: string; // "23:00"
  endDate: string; // ISO date string "2024-01-16" (can be next day)
  endTime: string; // "02:00"
  duration: number; // Total minutes
  crossesMidnight: boolean;
  availabilityStatus: 'available' | 'booked' | 'blocked' | 'maintenance';
}

// Enhanced booking request for cross-day support
export interface BookingRequest {
  startDateTime: string; // ISO datetime "2024-01-15T23:00:00Z"
  endDateTime: string; // ISO datetime "2024-01-16T02:00:00Z"
  duration: number; // Total minutes (180 for 3-hour session)
  crossesMidnight: boolean;
  affectedDays: string[]; // ["2024-01-15", "2024-01-16"]
}

// Validation result for booking requests
export interface ValidationResult {
  valid: boolean;
  error?: string;
  warnings?: string[];
}

export interface AvailabilitySettings {
  organizationId: string;
  workingHours: WorkingHours;
  timeSlotDuration: number; // in minutes (15, 30, 60, etc.)
  timeSlotUnit: '15min' | '30min' | '1hr' | '1day';
  advanceBookingDays: number; // how many days in advance can be booked
  minBookingNotice: number; // minimum hours before booking
  maxBookingDuration: number; // maximum session duration in minutes
  holidayDates: string[]; // ISO date strings for holidays/closures
  specialDates: SpecialDate[]; // Special opening hours for specific dates
  autoApproveBookings?: boolean; // Optional - approval determined by payment mode
  allowBackToBackBookings: boolean;
  bufferTimeBetweenBookings: number; // minutes between bookings
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export interface SpecialDate {
  id: string;
  date: string; // ISO date string
  name: string; // "Christmas Eve", "Extended Hours", etc.
  isWorkingDay: boolean;
  customHours?: {
    startTime: string;
    endTime: string;
  };
}

export interface RoomAvailability {
  id: string;
  roomId: string;
  organizationId: string;
  workingHours?: WorkingHours; // Override organization hours
  customSettings?: Partial<AvailabilitySettings>; // Override organization settings
  isActive: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export interface BookingRules {
  // Time settings
  timeIncrement?: number; // in minutes (15, 30, 60)
  minBookingDuration?: number; // in minutes
  maxBookingDuration?: number; // in minutes

  // Advance booking settings
  advanceBookingDays?: number; // how many days in advance
  advanceBookingHours?: number; // minimum hours in advance

  // Booking limits
  maxBookingsPerDay?: number;
  maxBookingsPerWeek?: number;
  maxBookingsPerCustomer?: number;

  // Staff and resource requirements
  requireStaffSelection?: boolean;
  requireResourceSelection?: boolean;
  allowMultipleBookings?: boolean;
  allowWaitlist?: boolean;

  // Cancellation policy
  allowCancellation?: boolean; // Whether cancellations are allowed
  cancellationHours?: number; // hours before booking
  cancellationFee?: number; // percentage or flat fee

  // Confirmation settings
  requireApproval?: boolean; // Require manual approval for bookings
  requireConfirmation?: boolean;
  autoConfirmBookings?: boolean;

  // Booking spacing
  allowBackToBackBookings?: boolean; // Allow consecutive bookings without gap
  bufferTimeBetweenBookings?: number; // Minutes gap between bookings
}

/**
 * Payment Settings for Bookings
 * Defines how payments are collected and processed for bookings
 */
export interface PaymentSettings {
  /**
   * Payment mode determines when and how payment is collected
   * - 'manual': No payment required, bookings confirmed manually by owner
   * - 'full': Full payment required upfront to confirm booking
   * - 'deposit': Partial payment (deposit) required, balance due later
   * - 'hold': Payment authorization hold (pre-auth), charged after owner confirms
   */
  paymentMode: 'manual' | 'full' | 'deposit' | 'hold';

  /**
   * Deposit percentage (0-100) when paymentMode is 'deposit'
   * Example: 50 means customer pays 50% upfront, 50% later
   */
  depositPercentage?: number;

  /**
   * Restrict deposit requirement to advance bookings only
   * When true, deposit is only required for bookings made X days in advance
   * For last-minute bookings (within threshold), full payment may be required
   */
  restrictDepositToAdvanceDays?: boolean;

  /**
   * Number of days threshold for deposit requirement
   * Only used when restrictDepositToAdvanceDays is true
   * Example: 7 means deposit only required for bookings made 7+ days in advance
   */
  depositOnlyForAdvanceDays?: number;

  /**
   * Restrict deposit requirement to specific days of the week
   * When enabled, deposit is only required for bookings on selected days
   * For other days, full payment may be required
   */
  restrictDepositToDaysOfWeek?: boolean;

  /**
   * Days of the week that require deposit (0-6, where 0=Sunday, 6=Saturday)
   * Only used when restrictDepositToDaysOfWeek is true
   * Example: [6] means deposit only required for Saturday bookings
   * Example: [5, 6] means deposit required for Friday and Saturday bookings
   */
  depositOnlyForDaysOfWeek?: number[];

  /**
   * Payment methods accepted by the organization
   */
  acceptCash?: boolean; // Accept cash payments on-site
  acceptCard?: boolean; // Accept card payments on-site (terminal)
  acceptOnlinePayment?: boolean; // Accept online payments via Stripe

  /**
   * Stripe integration settings
   * Required when acceptOnlinePayment is true
   */
  stripeAccountId?: string; // Stripe Connect account ID
  stripePublishableKey?: string; // Stripe publishable key

  /**
   * Payment hold settings (when paymentMode is 'hold')
   */
  holdExpirationDays?: number; // Days before hold expires (default: 7)
  autoChargeOnConfirm?: boolean; // Automatically charge when owner confirms

  /**
   * Refund and cancellation payment settings
   */
  allowRefunds?: boolean; // Whether refunds are permitted
  refundPolicy?: RefundPolicy; // Refund policy rules
  processingFeeRefundable?: boolean; // Whether platform fees are refundable
}

/**
 * Refund policy configuration
 */
export interface RefundPolicy {
  /**
   * Refund type
   * - 'full': Full refund if cancelled within window
   * - 'partial': Partial refund based on percentage
   * - 'none': No refunds allowed
   */
  type: 'full' | 'partial' | 'none';

  /**
   * Refund percentage (0-100) for partial refunds
   */
  refundPercentage?: number;

  /**
   * Hours before booking when full/partial refund is available
   */
  refundWindowHours?: number;

  /**
   * Graduated refund schedule
   * Different refund percentages based on cancellation timing
   */
  graduatedRefunds?: GraduatedRefund[];
}

/**
 * Graduated refund configuration
 * Allows different refund amounts based on when cancellation occurs
 */
export interface GraduatedRefund {
  /**
   * Hours before booking
   */
  hoursBeforeBooking: number;

  /**
   * Refund percentage (0-100)
   */
  refundPercentage: number;
}

export interface CustomField {
  id: string;
  name: string;
  type:
    | 'text'
    | 'number'
    | 'email'
    | 'phone'
    | 'select'
    | 'multiselect'
    | 'boolean'
    | 'date'
    | 'time'
    | 'textarea';
  required?: boolean;
  options?: string[]; // for select/multiselect
  validation?: FieldValidation;
  description?: string;
  placeholder?: string;
}

// Booking Types
export interface Booking {
  id: string;
  organizationId: string;
  roomId?: string; // Primary room being booked (optional for backwards compat)
  listingId?: string; // Main resource ID (room, service, etc.) - used by backend
  staffIds?: string[]; // Engineer/staff IDs assigned to booking (matches backend)
  equipmentIds?: string[]; // Additional equipment
  supportedServiceIds?: string[]; // Additional services

  // Customer Information
  customerName: string;
  customerEmail: string;
  customerPhone?: string;

  // Booking Time & Duration
  startDateTime: Timestamp | Date;
  endDateTime: Timestamp | Date;
  duration: number; // in minutes
  timeUnit: string; // from service settings

  // Status & Payment
  status: BookingStatus;
  paymentStatus: PaymentStatus;

  // Payment Details
  totalPrice: number; // Total booking cost
  paidAmount: number; // Amount already paid
  depositAmount?: number; // Required deposit amount (if deposit mode)
  remainingBalance?: number; // Balance still owed
  paymentMode?: 'manual' | 'full' | 'deposit' | 'hold'; // Payment mode used
  paymentMethodUsed?: 'cash' | 'card' | 'online' | 'pending'; // How customer paid/will pay

  // Payment Hold Information (for 'hold' payment mode)
  paymentHoldId?: string; // Stripe payment intent ID or hold reference (deprecated - use stripePaymentIntentId)
  stripePaymentIntentId?: string; // Stripe payment intent ID (primary field for hold mode)
  paymentHoldExpiry?: Timestamp | Date; // When the hold expires
  paymentHoldAmount?: number; // Amount on hold

  // Payment Transaction History
  paymentTransactions?: PaymentTransaction[]; // History of payments made

  // Refund Information
  refundAmount?: number; // Total amount refunded
  refundReason?: string; // Reason for refund
  refundedAt?: Timestamp | Date; // When refund was issued

  // Notes
  notes?: string; // Customer visible notes
  internalNotes?: string; // Staff-only notes
  purpose?: string; // "Recording session", "Mixing", etc.
  specialRequirements?: string;

  // System Fields
  isDeleted?: boolean; // For soft delete
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  createdBy?: string; // User ID who created the booking
  updatedBy?: string; // User ID who last updated the booking

  // Approval metadata
  confirmedBy?: string; // User ID who confirmed the booking (for manual approval)
  confirmedAt?: Timestamp | Date; // When booking was confirmed
  rejectedBy?: string; // User ID who rejected the booking
  rejectedAt?: Timestamp | Date; // When booking was rejected
  rejectionReason?: string; // Reason for rejection (visible to customer)
}

export type BookingStatus =
  | 'pending'           // Awaiting payment completion (online payment mode)
  | 'pending_approval'  // Awaiting owner approval (manual payment mode)
  | 'confirmed'         // Confirmed and scheduled
  | 'in-progress'       // Currently happening
  | 'completed'         // Finished successfully
  | 'cancelled'         // Cancelled by customer or owner
  | 'rejected'          // Rejected by owner
  | 'no-show';          // Customer didn't show up

export type PaymentStatus =
  | 'unpaid'            // No payment made yet
  | 'pending'           // Payment initiated but not completed
  | 'deposit_paid'      // Deposit paid, balance due
  | 'on_hold'           // Payment authorization hold placed
  | 'paid'              // Fully paid
  | 'partial'           // Partially paid (legacy, use deposit_paid)
  | 'refunded'          // Fully refunded
  | 'partial_refund';   // Partially refunded

/**
 * Payment transaction types
 */
export type PaymentTransactionType =
  | 'deposit'           // Initial deposit payment
  | 'full_payment'      // Full payment upfront
  | 'balance'           // Remaining balance payment
  | 'hold'              // Payment authorization hold
  | 'capture'           // Capturing a held payment
  | 'refund'            // Refunding a payment
  | 'partial_refund';   // Partial refund

/**
 * Payment transaction record
 * Tracks individual payment events for a booking
 */
export interface PaymentTransaction {
  id: string;
  bookingId: string;
  organizationId: string;

  // Transaction Details
  type: PaymentTransactionType;
  amount: number;
  currency: string; // ISO currency code (e.g., 'EUR', 'USD')

  // Payment Method
  paymentMethod: 'cash' | 'card' | 'online' | 'bank_transfer' | 'other';
  paymentProvider?: 'stripe' | 'manual' | 'other';

  // Transaction Status
  status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';

  // External References
  stripePaymentIntentId?: string; // Stripe payment intent ID
  stripeChargeId?: string; // Stripe charge ID
  externalReference?: string; // Other payment provider reference

  // Metadata
  description?: string;
  receiptUrl?: string; // Link to payment receipt
  notes?: string; // Internal notes about the transaction

  // Timestamps
  createdAt: Timestamp | Date;
  processedAt?: Timestamp | Date; // When payment was actually processed
  refundedAt?: Timestamp | Date; // When refund was issued

  // User tracking
  processedBy?: string; // User ID who processed the payment
}

export interface BookingPriceBreakdown {
  roomRate: number;
  engineerRate?: number;
  equipmentRate?: number;
  servicesRate?: number;
  duration: number; // in hours
  subtotal: number;
  taxes?: number;
  discounts?: number;
  total: number;
}

// API Request/Response Types for Bookings
export interface CreateBookingRequest {
  organizationId: string;
  roomId: string;
  engineerId?: string;
  equipmentIds?: string[];
  supportedServiceIds?: string[];
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  startDateTime: string; // ISO string
  endDateTime: string; // ISO string
  duration: number;
  totalPrice: number;
  notes?: string;
  purpose?: string;
  specialRequirements?: string;
  // Manual booking fields
  paymentMethodUsed?: 'cash' | 'card' | 'online' | 'pending'; // How customer paid/will pay
  status?: BookingStatus; // Allow setting initial status for manual bookings
  paymentStatus?: PaymentStatus; // Allow setting initial payment status
  paidAmount?: number; // Amount already paid
}

export interface UpdateBookingRequest extends Partial<CreateBookingRequest> {
  id: string;
  status?: BookingStatus;
  paymentStatus?: PaymentStatus;
  paidAmount?: number;
  internalNotes?: string;
}

export interface BookingResponse {
  booking: Booking;
}

export interface BookingsResponse {
  bookings: Booking[];
  total: number;
}

export interface BookingFilters {
  organizationId?: string;
  roomId?: string;
  engineerId?: string;
  customerEmail?: string;
  status?: BookingStatus | BookingStatus[];
  paymentStatus?: PaymentStatus;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

// Equipment Management Types
export interface Equipment {
  id: string;
  organizationId: string;
  name: string;
  type: EquipmentType;
  description?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  hourlyRate: number;
  dailyRate?: number;
  weeklyRate?: number;
  available: boolean;
  condition: EquipmentCondition;
  image?: string;
  images?: string[];
  specifications?: EquipmentSpecification[];
  maintenanceHistory?: MaintenanceRecord[];
  lastMaintenanceDate?: Timestamp | Date;
  nextMaintenanceDate?: Timestamp | Date;
  purchaseDate?: Timestamp | Date;
  purchasePrice?: number;
  warrantyExpires?: Timestamp | Date;
  location?: string; // Room or storage location
  notes?: string; // Internal notes

  // Showcase-specific fields
  showcaseEnabled?: boolean; // Whether this equipment is featured in the showcase
  showcaseTitle?: string; // Custom title for showcase (different from internal name)
  showcaseDescription?: string; // Marketing-friendly description for showcase
  showcaseTags?: string[]; // Tags for categorization (e.g., "professional", "vintage", "high-end")
  showcaseOrder?: number; // Order in the showcase display
  featuredSpecs?: string[]; // Key specifications to highlight in showcase
  marketingImages?: string[]; // Professional photos for showcase (separate from internal documentation)
  demoTracks?: string[]; // URLs to demo recordings made with this equipment
  notableProjects?: string[]; // Names of notable projects this equipment was used on
  artistEndorsements?: string[]; // Quotes or endorsements from artists who used this equipment

  isDeleted?: boolean;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

export type EquipmentType =
  | 'Microphone'
  | 'Outboard'
  | 'Amplifier'
  | 'Synthesizer'
  | 'Interface'
  | 'Monitor'
  | 'Headphones'
  | 'Instrument'
  | 'Cable'
  | 'Stand'
  | 'Rack'
  | 'Computer'
  | 'Software'
  | 'Other';

export type EquipmentCondition = 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Repair Needed';

export interface EquipmentSpecification {
  name: string;
  value: string;
}

export interface MaintenanceRecord {
  id: string;
  date: Timestamp | Date;
  type: 'Routine' | 'Repair' | 'Upgrade' | 'Inspection';
  description: string;
  cost?: number;
  performedBy: string; // Staff member or external service
  nextMaintenanceDue?: Timestamp | Date;
  attachments?: string[]; // File URLs
}

// Equipment API Request/Response Types
export interface CreateEquipmentRequest {
  organizationId: string;
  name: string;
  type: EquipmentType;
  description?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  hourlyRate: number;
  dailyRate?: number;
  weeklyRate?: number;
  available?: boolean;
  condition: EquipmentCondition;
  image?: string;
  specifications?: EquipmentSpecification[];
  purchaseDate?: string; // ISO string
  purchasePrice?: number;
  warrantyExpires?: string; // ISO string
  location?: string;
  notes?: string;
}

export interface UpdateEquipmentRequest extends Partial<CreateEquipmentRequest> {
  id: string;
}

export interface EquipmentResponse {
  equipment: Equipment;
}

export interface EquipmentListResponse {
  equipment: Equipment[];
  total: number;
}

export interface EquipmentFilters {
  organizationId?: string;
  type?: EquipmentType | EquipmentType[];
  available?: boolean;
  condition?: EquipmentCondition;
  location?: string;
  search?: string; // Search in name, brand, model, description
  limit?: number;
  offset?: number;
}

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  pattern?: string; // regex pattern
  min?: number; // for number fields
  max?: number; // for number fields
}

// Type-specific settings
export interface EventSettings {
  allowTicketSales?: boolean;
  requireAgeVerification?: boolean;
  enableSeatSelection?: boolean;
  enablePromoterProgram?: boolean;
  defaultEventDuration?: number; // in hours
  enablePassCulture?: boolean;
}

export interface RestaurantSettings {
  enableTableReservations?: boolean;
  enableOnlineOrdering?: boolean;
  requireDeposit?: boolean;
  depositAmount?: number;
  maxPartySize?: number;
  enableWaitlist?: boolean;
  integrations?: RestaurantIntegrations;
}

export interface RestaurantIntegrations {
  zelty?: ZeltyIntegration;
  stripe?: StripeIntegration;
}

export interface ZeltyIntegration {
  enabled: boolean;
  apiKey?: string;
  storeId?: string;
  syncMenus?: boolean;
  syncOrders?: boolean;
}

export interface StripeIntegration {
  enabled: boolean;
  accountId?: string;
  publishableKey?: string;
}

export interface ServiceSettings {
  enableOnlineBooking?: boolean;
  requireClientRegistration?: boolean;
  enablePackages?: boolean;
  enableMemberships?: boolean;
  allowRecurringAppointments?: boolean;
  enableReminders?: boolean;
  reminderSettings?: ReminderSettings;
}

export interface ReminderSettings {
  email?: boolean;
  sms?: boolean;
  hoursBeforeAppointment?: number[];
}

export interface ThemeSettings {
  primaryColor?: string;
  secondaryColor?: string;
  logoPosition?: 'left' | 'center' | 'right';
  customCss?: string;
}

export interface ChannelNotifications {
  isActive?: boolean; // Whether this channel is enabled
  bookingConfirmation?: boolean; // Notify client after booking is completed
  bookingReminder?: boolean; // Notify client to confirm booking
  bookingStatusUpdate?: boolean; // Notify client if booking status changes
  ownerBookingAlert?: boolean; // Notify owner of new booking
  ownerCancellationAlert?: boolean; // Notify owner of cancelled booking
}

export interface NotificationSettings {
  email?: ChannelNotifications;
  sms?: ChannelNotifications;
  whatsapp?: ChannelNotifications;
}

// Staff and roles for enhanced organizations
export interface OrganisationStaffRole {
  id?: string;
  organizationId: string;
  name: string;
  permissions: RolePermission[];
  description?: string;
  isDefault?: boolean; // System-defined roles vs custom roles
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export type RolePermission =
  // General permissions
  | 'view_dashboard'
  | 'manage_bookings'
  | 'manage_customers'
  | 'manage_staff'
  | 'view_reports'
  | 'manage_settings'

  // Event-specific permissions
  | 'create_events'
  | 'edit_events'
  | 'manage_tickets'
  | 'view_sales'
  | 'manage_reservations'

  // Restaurant-specific permissions
  | 'manage_tables'
  | 'manage_menu'
  | 'process_orders'
  | 'manage_inventory'

  // Service-specific permissions
  | 'schedule_appointments'
  | 'manage_services'
  | 'view_client_records'
  | 'process_payments'

  // Admin permissions
  | 'full_access'
  | 'billing_access'
  | 'integration_access';

export interface OrganisationStaffMember {
  id?: string;
  organizationId: string;
  ownerId: string;
  name: string;
  email: string;
  password?: string; // For initial setup only
  picture?: string;
  roleId?: string; // Reference to OrganisationStaffRole
  roleName?: string; // Legacy compatibility

  // Service-specific fields
  supportedServices?: string[]; // Services this staff member can provide
  specialties?: string[]; // Areas of expertise
  workingHours?: WorkingHours; // Individual schedule

  // Contact and personal info
  phone?: string;
  address?: string;
  dateOfBirth?: Date;
  hireDate?: Date;

  // Status and permissions
  isActive?: boolean;
  isVerified?: boolean;
  lastLoginAt?: Timestamp | Date;

  // Settings
  notifications?: NotificationSettings;

  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

// Onboarding flow types
export interface OrganisationOnboardingData {
  // Step 1: Basic Information
  basicInfo: {
    name: string;
    description: string;
    language: 'en' | 'fr';
    logo?: string;
    coverImage?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    contact?: Partial<OrganisationContact>;
  };

  // Step 2: Organization Type Selection
  typeSelection: {
    type: EnhancedOrgType;
    customSettings?: Partial<OrganisationSettings>;
  };

  // Step 3: Staff Roles Setup (Optional)
  rolesSetup: {
    useDefaultRoles: boolean;
    customRoles: string[];
    selectedDefaultRoles: string[];
  };

  // Step 4: Staff Members Setup (Optional)
  staffSetup: {
    staffMembers: Omit<
      OrganisationStaffMember,
      'id' | 'organizationId' | 'ownerId' | 'createdAt' | 'updatedAt'
    >[];
  };

  // Step 5: Features and Settings (Optional)
  featuresSetup: {
    selectedFeatures: OrganisationFeature[];
    businessHours?: WorkingHours;
    bookingRules?: Partial<BookingRules>;
    availabilitySettings?: Partial<AvailabilitySettings>;
  };
}

// Organization presets for different types
export interface OrganisationPreset {
  type: EnhancedOrgType;
  label: string;
  description: string;
  icon: string;
  color: string;
  category: 'existing' | 'new';

  // Default configuration
  defaultSettings: OrganisationSettings;
  defaultRoles: string[];
  requiredFeatures: OrganisationFeature[];
  optionalFeatures: OrganisationFeature[];

  // Onboarding flow customization
  skipSteps?: ('roles' | 'staff' | 'features')[];
  customSteps?: string[];

  // Integration requirements
  requiredIntegrations?: string[];
  recommendedIntegrations?: string[];
}

// Type guards for enhanced organizations
export const isEnhancedOrganisation = (org: any): org is EnhancedOrganisation =>
  org &&
  typeof org.type === 'string' &&
  [
    'evenementiel',
    'restaurant',
    'recording_studio',
    'service',
    'commerce',
    'healthcare',
    'education',
    'fitness',
    'beauty',
    'automotive',
    'professional',
  ].includes(org.type);

export const isLegacyOrganisation = (org: any): boolean =>
  org && (org.type === 'Evenementiel' || org.type === 'Restaurant');

// Migration helpers
export const convertLegacyToEnhanced = (legacyOrg: any): EnhancedOrganisation => ({
  id: legacyOrg.id,
  name: legacyOrg.name,
  description: legacyOrg.bio || legacyOrg.description || '',
  bio: legacyOrg.bio,
  type:
    legacyOrg.type === 'Evenementiel'
      ? 'evenementiel'
      : legacyOrg.type === 'Restaurant'
      ? 'restaurant'
      : 'service',
  ownerId: legacyOrg.ownerid || legacyOrg.ownerId,
  ownerid: legacyOrg.ownerid,
  logoImage: legacyOrg.logoImage,
  logo: legacyOrg.logoImage,
  coverImage: legacyOrg.coverImage,
  contact: legacyOrg.contact,
  followers: legacyOrg.followers,
  totalEvents: legacyOrg.totalEvents,
  address: legacyOrg.address,
  legalInfos: legacyOrg.legalInfos,
  siren: legacyOrg.siren,
  siret: legacyOrg.siret,
  companyNumber: legacyOrg.companyNumber,
  groupeTable: legacyOrg.groupeTable,
  isDeleted: legacyOrg.isDeleted,
  createdAt: legacyOrg.createdAt,
  updatedAt: legacyOrg.updatedAt,
});

// Utility functions
export const getOrganisationTypeLabel = (
  type: EnhancedOrgType,
  language: 'en' | 'fr' = 'en'
): string => {
  const labels: Record<EnhancedOrgType, { en: string; fr: string }> = {
    evenementiel: { en: 'Events & Entertainment', fr: 'Événementiel & Divertissement' },
    restaurant: { en: 'Restaurant & Food Service', fr: 'Restaurant & Service Alimentaire' },
    recording_studio: { en: 'Music/Recording Studios', fr: "Studios d'Enregistrement Musical" },
    service: { en: 'Professional Services', fr: 'Services Professionnels' },
    commerce: { en: 'Retail & Commerce', fr: 'Commerce & Vente au Détail' },
    healthcare: { en: 'Healthcare & Medical', fr: 'Santé & Médical' },
    education: { en: 'Education & Training', fr: 'Éducation & Formation' },
    fitness: { en: 'Fitness & Sports', fr: 'Fitness & Sport' },
    beauty: { en: 'Beauty & Wellness', fr: 'Beauté & Bien-être' },
    automotive: { en: 'Automotive Services', fr: 'Services Automobiles' },
    professional: { en: 'Professional Services', fr: 'Services Professionnels' },
  };

  return labels[type][language];
};

export const getOrganisationTypeIcon = (type: EnhancedOrgType): string => {
  const icons: Record<EnhancedOrgType, string> = {
    evenementiel: 'mdi:party-popper',
    restaurant: 'mdi:silverware-fork-knife',
    recording_studio: 'mdi:microphone',
    service: 'mdi:briefcase',
    commerce: 'mdi:store',
    healthcare: 'mdi:medical-bag',
    education: 'mdi:school',
    fitness: 'mdi:dumbbell',
    beauty: 'mdi:spa',
    automotive: 'mdi:car',
    professional: 'mdi:tie',
  };

  return icons[type];
};

export const getOrganisationTypeColor = (type: EnhancedOrgType): string => {
  const colors: Record<EnhancedOrgType, string> = {
    evenementiel: '#8B5CF6', // Purple
    restaurant: '#F59E0B', // Amber
    recording_studio: '#DC2626', // Red (music/sound theme)
    service: '#3B82F6', // Blue
    commerce: '#10B981', // Emerald
    healthcare: '#EF4444', // Red
    education: '#6366F1', // Indigo
    fitness: '#F97316', // Orange
    beauty: '#EC4899', // Pink
    automotive: '#6B7280', // Gray
    professional: '#1F2937', // Dark Gray
  };

  return colors[type];
};

// Room management interfaces for recording studios
export interface Room {
  id: string;
  organizationId: string;
  name: string;
  capacity: number;
  hourlyRate: number;
  description?: string;
  features: string[];
  equipment: string[];
  images?: string[]; // URLs of room images
  thumbnailImage?: string; // Main thumbnail image URL
  isActive: boolean;
  isDeleted?: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export interface CreateRoomRequest {
  organizationId: string;
  name: string;
  capacity: number;
  hourlyRate: number;
  description?: string;
  features: string[];
  equipment: string[];
  images?: string[];
  thumbnailImage?: string;
  isActive: boolean;
}

export interface UpdateRoomRequest {
  organizationId: string;
  name?: string;
  capacity?: number;
  hourlyRate?: number;
  description?: string;
  features?: string[];
  equipment?: string[];
  images?: string[];
  thumbnailImage?: string;
  isActive?: boolean;
}

export interface RoomResponse {
  room: Room;
}

export interface RoomsResponse {
  rooms: Room[];
}

// ==================== AVAILABILITY REQUEST/RESPONSE INTERFACES ====================

export interface CreateAvailabilitySettingsRequest {
  organizationId: string;
  workingHours: WorkingHours;
  timeSlotDuration: number;
  timeSlotUnit: '15min' | '30min' | '1hr' | '1day';
  advanceBookingDays: number;
  minBookingNotice: number;
  maxBookingDuration: number;
  holidayDates: string[];
  specialDates: SpecialDate[];
  autoApproveBookings?: boolean; // Optional - approval determined by payment mode
  allowBackToBackBookings: boolean;
  bufferTimeBetweenBookings: number;
}

export interface UpdateAvailabilitySettingsRequest
  extends Partial<CreateAvailabilitySettingsRequest> {
  organizationId: string;
}

export interface AvailabilitySettingsResponse {
  settings: AvailabilitySettings;
}

export interface CreateRoomAvailabilityRequest {
  roomId: string;
  organizationId: string;
  workingHours?: WorkingHours;
  customSettings?: Partial<AvailabilitySettings>;
  isActive: boolean;
}

export interface UpdateRoomAvailabilityRequest extends Partial<CreateRoomAvailabilityRequest> {
  roomId: string;
  organizationId: string;
}

export interface RoomAvailabilityResponse {
  availability: RoomAvailability;
}

export interface GetAvailableTimeSlotsRequest {
  organizationId: string;
  roomId: string;
  date: string; // ISO date string
  duration: number; // in minutes
}

export interface GetAvailableTimeSlotsResponse {
  date: string;
  availableSlots: string[]; // Array of time strings like ["09:00", "10:00", "11:00"]
  workingHours: {
    isWorkingDay: boolean;
    startTime?: string;
    endTime?: string;
  };
}

// Room feature constants
export const ROOM_FEATURES = [
  'Isolation Booth',
  'Control Room',
  'Live Room',
  'Vocal Booth',
  'Reverb Chamber',
  'Piano Room',
  'Drum Room',
  'Mixing Room',
  'Mastering Suite',
  'Lounge Area',
] as const;

export type RoomFeature = typeof ROOM_FEATURES[number];

// ==================== ENGINEER MANAGEMENT TYPES ====================

// Engineer management interfaces for recording studios
export interface Engineer {
  id: string;
  organizationId: string;
  name: string;
  email: string;
  phone?: string;
  bio?: string; // Professional biography
  specialties: string[]; // e.g., "Mixing", "Mastering", "Recording", "Live Sound"
  hourlyRate: number;
  experience?: string; // Years of experience or description
  profileImage?: string; // URL of profile image
  portfolio?: EngineerPortfolio; // Links to work samples
  certifications?: string[]; // Professional certifications
  color?: string; // Hex color for calendar display (e.g., "#FF5722")
  isActive: boolean;
  isAvailable: boolean; // Quick toggle for availability
  isDeleted?: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;

  // Denormalized availability for fast booking queries
  // This duplicates data from availability_settings subcollection for performance
  quickAvailability?: {
    isAvailable: boolean;
    useStudioAvailability: boolean;
    workingHours?: WorkingHours;
    unavailableDates?: string[];
    lastUpdated?: Timestamp | Date;
  };
}

export interface EngineerPortfolio {
  websiteUrl?: string;
  soundcloudUrl?: string;
  spotifyUrl?: string;
  youtubeUrl?: string;
  linkedinUrl?: string;
  otherLinks?: string[];
  featuredProjects?: FeaturedProject[];
}

export interface FeaturedProject {
  id: string;
  title: string;
  artist?: string;
  description?: string;
  role: string; // "Recording Engineer", "Mix Engineer", etc.
  year?: number;
  audioUrl?: string; // Sample audio
  imageUrl?: string;
}

// Weekly schedule for engineer availability
export interface WeeklySchedule {
  id: string;
  weekStartDate: string; // ISO date string for Monday of the week (e.g., "2024-01-15")
  workingHours: WorkingHours; // The schedule for this specific week
  notes?: string; // Optional notes (e.g., "Holiday week", "Peak season", "Special hours")
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

// Engineer availability (similar to room availability)
// NOTE: Weekly schedules are now stored as individual documents in the same collection
// with document IDs as date strings (YYYY-MM-DD) instead of in an array
export interface EngineerAvailability {
  id: string;
  engineerId: string;
  organizationId: string;

  // Availability mode
  useStudioAvailability: boolean; // If true, inherit studio's availability settings

  // Custom availability settings (only used when useStudioAvailability is false)
  workingHours?: WorkingHours; // Default/fallback schedule (used when no weekly schedule exists)
  customSettings?: Partial<AvailabilitySettings>; // Override organization settings

  // Unavailable dates (applies regardless of availability mode)
  unavailableDates?: string[]; // ISO date strings for days off/vacation

  isActive: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

// Individual week schedule document
// Stored in: organisers/{orgId}/engineers/{engineerId}/availability_settings/{weekStartDate}
// e.g., organisers/abc123/engineers/eng456/availability_settings/2024-01-15
export interface WeeklyScheduleDocument {
  id: string; // Week start date in YYYY-MM-DD format (Monday)
  engineerId: string;
  organizationId: string;
  weekStartDate: string; // ISO date string (Monday of the week)
  weekEndDate: string; // ISO date string (Sunday of the week)
  schedule: WeeklySchedule; // The actual schedule data
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

// Engineer specialties constants
export const ENGINEER_SPECIALTIES = [
  'Recording',
  'Mixing',
  'Mastering',
  'Live Sound',
  'Music Production',
  'Audio Post-Production',
  'Sound Design',
  'Vocal Production',
  'Beat Making',
  'Podcast Production'
] as const;

export type EngineerSpecialty = typeof ENGINEER_SPECIALTIES[number];

// API Request/Response Types for Engineers
export interface CreateEngineerRequest {
  organizationId: string;
  name: string;
  email: string;
  phone?: string;
  bio?: string;
  specialties: string[];
  hourlyRate: number;
  experience?: string;
  profileImage?: string;
  portfolio?: EngineerPortfolio;
  certifications?: string[];
  isActive: boolean;
  isAvailable?: boolean;
}

export interface UpdateEngineerRequest extends Partial<CreateEngineerRequest> {
  id: string;
}

export interface EngineerResponse {
  engineer: Engineer;
}

export interface EngineersResponse {
  engineers: Engineer[];
  total: number;
}

export interface EngineerFilters {
  organizationId?: string;
  specialties?: string | string[];
  isActive?: boolean;
  isAvailable?: boolean;
  search?: string; // Search in name, bio, specialties
  limit?: number;
  offset?: number;
}

// Engineer Availability API Types
export interface CreateEngineerAvailabilityRequest {
  engineerId: string;
  organizationId: string;
  useStudioAvailability: boolean; // Whether to inherit studio availability
  workingHours?: WorkingHours; // Custom schedule (only if useStudioAvailability is false)
  customSettings?: Partial<AvailabilitySettings>; // Custom settings (only if useStudioAvailability is false)
  isActive: boolean;
  unavailableDates?: string[]; // Days off (always applies)
}

export interface UpdateEngineerAvailabilityRequest extends Partial<CreateEngineerAvailabilityRequest> {
  engineerId: string;
  organizationId: string;
}

// Weekly Schedule API Types (for individual week document operations)
export interface UpsertWeekScheduleRequest {
  engineerId: string;
  organizationId: string;
  weekStartDate: string; // YYYY-MM-DD format (Monday)
  schedule: WeeklySchedule;
}

export interface DeleteWeekScheduleRequest {
  engineerId: string;
  organizationId: string;
  weekStartDate: string; // YYYY-MM-DD format (Monday)
}

export interface GetWeekScheduleRequest {
  engineerId: string;
  organizationId: string;
  weekStartDate: string; // YYYY-MM-DD format (Monday)
}

export interface ListWeekSchedulesRequest {
  engineerId: string;
  organizationId: string;
  startDate?: string; // Optional: filter from this date
  endDate?: string; // Optional: filter to this date
}

export interface EngineerAvailabilityResponse {
  availability: EngineerAvailability;
}

export interface GetAvailableEngineersRequest {
  organizationId: string;
  startDateTime: string; // ISO string
  endDateTime: string;   // ISO string
  specialty?: string; // Filter by specialty
}

export interface GetAvailableEngineersResponse {
  engineers: Engineer[];
  availableSlots: {
    [engineerId: string]: {
      isAvailable: boolean;
      reason?: string; // "booked", "off-hours", "unavailable-date"
    };
  };
}

// ==================== MUSIC SHOWCASE TYPES ====================

export interface MusicShowcase {
  id?: string;
  organizationId: string;
  isEnabled: boolean;
  title?: string;
  description?: string;
  tracks: MusicTrack[];
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export interface MusicTrack {
  id: string;
  title: string;
  artist?: string;
  description?: string;
  platform: MusicPlatform;
  url: string;
  embedCode?: string; // For platforms that support embedding
  thumbnailUrl?: string;
  duration?: number; // in seconds
  order: number; // for ordering tracks in the showcase
  isActive: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export type MusicPlatform =
  | 'youtube'
  | 'spotify'
  | 'soundcloud'
  | 'apple_music'
  | 'bandcamp'
  | 'other';

export interface CreateMusicShowcaseRequest {
  organizationId: string;
  isEnabled: boolean;
  title?: string;
  description?: string;
}

export interface UpdateMusicShowcaseRequest extends Partial<CreateMusicShowcaseRequest> {
  id: string;
}

export interface CreateMusicTrackRequest {
  organizationId: string;
  showcaseId: string;
  title: string;
  artist?: string;
  description?: string;
  platform: MusicPlatform;
  url: string;
  order?: number;
  isActive?: boolean;
}

export interface UpdateMusicTrackRequest extends Partial<CreateMusicTrackRequest> {
  id: string;
}

export interface MusicShowcaseResponse {
  showcase: MusicShowcase;
}

export interface MusicTrackResponse {
  track: MusicTrack;
}

export interface MusicTracksResponse {
  tracks: MusicTrack[];
  total: number;
}

export interface MusicTrackFilters {
  organizationId?: string;
  showcaseId?: string;
  platform?: MusicPlatform | MusicPlatform[];
  isActive?: boolean;
  search?: string; // Search in title, artist, description
  limit?: number;
  offset?: number;
}

// Utility functions for music showcase
export const getMusicPlatformLabel = (platform: MusicPlatform): string => {
  const labels: Record<MusicPlatform, string> = {
    youtube: 'YouTube',
    spotify: 'Spotify',
    soundcloud: 'SoundCloud',
    apple_music: 'Apple Music',
    bandcamp: 'Bandcamp',
    other: 'Other',
  };
  return labels[platform];
};

export const getMusicPlatformIcon = (platform: MusicPlatform): string => {
  const icons: Record<MusicPlatform, string> = {
    youtube: 'mdi:youtube',
    spotify: 'mdi:spotify',
    soundcloud: 'mdi:soundcloud',
    apple_music: 'mdi:music',
    bandcamp: 'mdi:music-box',
    other: 'mdi:music-note',
  };
  return icons[platform];
};

export const getMusicPlatformColor = (platform: MusicPlatform): string => {
  const colors: Record<MusicPlatform, string> = {
    youtube: '#FF0000',
    spotify: '#1DB954',
    soundcloud: '#FF5500',
    apple_music: '#000000',
    bandcamp: '#629AA0',
    other: '#666666',
  };
  return colors[platform];
};

// URL validation patterns for different platforms
export const MUSIC_PLATFORM_PATTERNS: Record<MusicPlatform, RegExp> = {
  youtube: /^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/).+/,
  spotify: /^https?:\/\/open\.spotify\.com\/(track|album|playlist|artist)\/.+/,
  soundcloud: /^https?:\/\/(www\.)?soundcloud\.com\/.+/,
  apple_music: /^https?:\/\/music\.apple\.com\/.+/,
  bandcamp: /^https?:\/\/[^.]+\.bandcamp\.com\/track\/.+/,
  other: /.+/, // Accept any URL for 'other' platform
};

export const validateMusicUrl = (url: string, platform: MusicPlatform): boolean => {
  const pattern = MUSIC_PLATFORM_PATTERNS[platform];
  return pattern.test(url);
};

export const extractVideoIdFromYouTube = (url: string): string | null => {
  const patterns = [/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return null;
};

export const generateEmbedCode = (url: string, platform: MusicPlatform): string | null => {
  switch (platform) {
    case 'youtube':
      const videoId = extractVideoIdFromYouTube(url);
      if (videoId) {
        return `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe>`;
      }
      return null;
    case 'spotify':
      // Extract Spotify track/album/playlist ID from URL
      const spotifyMatch = url.match(/open\.spotify\.com\/(track|album|playlist)\/([a-zA-Z0-9]+)/);
      if (spotifyMatch) {
        const [, type, id] = spotifyMatch;
        return `<iframe src="https://open.spotify.com/embed/${type}/${id}" width="300" height="380" frameborder="0" allowtransparency="true" allow="encrypted-media"></iframe>`;
      }
      return null;
    case 'soundcloud':
      // SoundCloud requires their API to generate embed codes
      return null;
    default:
      return null;
  }
};

// ==================== STUDIO GALLERY TYPES ====================

export interface StudioGallery {
  id?: string;
  organizationId: string;
  isEnabled: boolean;
  photos: StudioPhoto[];
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export interface StudioPhoto {
  id: string;
  url: string;
  title: string;
  description?: string;
  category: StudioPhotoCategory;
  order: number;
  isActive: boolean;
  uploadedAt?: Timestamp | Date;
}

export type StudioPhotoCategory =
  | 'studio_rooms'
  | 'control_room'
  | 'equipment'
  | 'atmosphere'
  | 'exterior'
  | 'other';

export const STUDIO_PHOTO_CATEGORIES: Record<StudioPhotoCategory, { label: string; icon: string }> =
  {
    studio_rooms: { label: 'Studio Rooms', icon: 'mdi:home-music' },
    control_room: { label: 'Control Room', icon: 'mdi:mixer' },
    equipment: { label: 'Equipment', icon: 'mdi:microphone' },
    atmosphere: { label: 'Atmosphere', icon: 'mdi:lightbulb-on' },
    exterior: { label: 'Exterior', icon: 'mdi:home-outline' },
    other: { label: 'Other', icon: 'mdi:image' },
  };

export interface CreateStudioGalleryRequest {
  organizationId: string;
  isEnabled: boolean;
}

export interface UpdateStudioGalleryRequest {
  id: string;
  isEnabled?: boolean;
}

export interface CreateStudioPhotoRequest {
  organizationId: string;
  galleryId: string;
  title: string;
  description?: string;
  category: StudioPhotoCategory;
  url: string;
  order?: number;
  isActive?: boolean;
}

export interface UpdateStudioPhotoRequest extends Partial<CreateStudioPhotoRequest> {
  id: string;
}

export interface StudioGalleryResponse {
  gallery: StudioGallery;
}

export interface StudioPhotosResponse {
  photos: StudioPhoto[];
  total: number;
}

// ==================== STUDIO CERTIFICATIONS TYPES ====================

export interface StudioCertifications {
  id?: string;
  organizationId: string;
  isEnabled: boolean;
  certifications: Certification[];
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export interface Certification {
  id: string;
  type: CertificationType;
  title: string;
  artistName: string;
  projectName: string;
  dateAwarded: Date | Timestamp;
  awardImageUrl?: string;
  issuingOrganization?: string;
  description?: string;
  order: number;
  isActive: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export type CertificationType =
  | 'gold_record'
  | 'platinum_record'
  | 'diamond_record'
  | 'grammy'
  | 'industry_award'
  | 'certification'
  | 'achievement'
  | 'recognition'
  | 'other';

export const CERTIFICATION_TYPES: Record<
  CertificationType,
  { label: string; icon: string; color: string }
> = {
  gold_record: { label: 'Gold Record', icon: 'mdi:trophy', color: '#FFD700' },
  platinum_record: { label: 'Platinum Record', icon: 'mdi:trophy', color: '#E5E4E2' },
  diamond_record: { label: 'Diamond Record', icon: 'mdi:diamond-stone', color: '#B9F2FF' },
  grammy: { label: 'Grammy Award', icon: 'mdi:trophy-award', color: '#D4AF37' },
  industry_award: { label: 'Industry Award', icon: 'mdi:award', color: '#CD7F32' },
  certification: { label: 'Certification', icon: 'mdi:certificate', color: '#4169E1' },
  achievement: { label: 'Achievement', icon: 'mdi:medal', color: '#FF6347' },
  recognition: { label: 'Recognition', icon: 'mdi:star', color: '#9370DB' },
  other: { label: 'Other', icon: 'mdi:trophy-variant', color: '#696969' },
};

export interface CreateStudioCertificationsRequest {
  organizationId: string;
  isEnabled: boolean;
}

export interface CreateCertificationRequest {
  organizationId: string;
  certificationsId: string;
  type: CertificationType;
  title: string;
  artistName: string;
  projectName: string;
  dateAwarded: Date;
  awardImageUrl?: string;
  issuingOrganization?: string;
  description?: string;
  order?: number;
  isActive?: boolean;
}

export interface UpdateCertificationRequest extends Partial<CreateCertificationRequest> {
  id: string;
}

export interface StudioCertificationsResponse {
  certifications: StudioCertifications;
}

export interface CertificationsResponse {
  certifications: Certification[];
  total: number;
}

// ==================== FEATURED ARTISTS TYPES ====================

export interface ArtistGallery {
  id?: string;
  organizationId: string;
  isEnabled: boolean;
  featuredArtists: FeaturedArtist[];
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export interface FeaturedArtist {
  id: string;
  organizationId: string;
  name: string;
  genre: ArtistGenre;
  photoUrl?: string;
  description?: string;
  projectInfo?: string;
  collaborationDate?: Date | Timestamp;
  featuredAt?: Date | Timestamp;
  websiteUrl?: string;
  instagramUrl?: string;
  spotifyUrl?: string;
  youtubeUrl?: string;
  socialLinks?: {
    instagram?: string;
    twitter?: string;
    spotify?: string;
    youtube?: string;
  };
  hasConsent: boolean;
  consentStatus: ConsentStatus;
  order: number;
  isActive: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

export type ConsentStatus = 'granted' | 'pending' | 'denied' | 'not_required';

export const CONSENT_STATUS_LABELS: Record<
  ConsentStatus,
  { label: string; color: string; icon: string }
> = {
  granted: { label: 'Consent Granted', color: 'success', icon: 'mdi:check-circle' },
  pending: { label: 'Consent Pending', color: 'warning', icon: 'mdi:clock' },
  denied: { label: 'Consent Denied', color: 'error', icon: 'mdi:close-circle' },
  not_required: { label: 'Consent Not Required', color: 'info', icon: 'mdi:information' },
};

// Artist Genre types for categorizing featured artists
export type ArtistGenre =
  | 'pop'
  | 'rock'
  | 'hip_hop'
  | 'electronic'
  | 'jazz'
  | 'classical'
  | 'country'
  | 'folk'
  | 'blues'
  | 'reggae'
  | 'punk'
  | 'metal'
  | 'indie'
  | 'world'
  | 'experimental'
  | 'other';

export const ARTIST_GENRES: Record<ArtistGenre, { label: string; icon: string; color: string }> = {
  pop: { label: 'Pop', icon: 'mdi:music-note', color: '#FF6B6B' },
  rock: { label: 'Rock', icon: 'mdi:guitar-electric', color: '#4ECDC4' },
  hip_hop: { label: 'Hip Hop', icon: 'mdi:microphone', color: '#45B7D1' },
  electronic: { label: 'Electronic', icon: 'mdi:console', color: '#96CEB4' },
  jazz: { label: 'Jazz', icon: 'mdi:saxophone', color: '#FFEAA7' },
  classical: { label: 'Classical', icon: 'mdi:piano', color: '#DDA0DD' },
  country: { label: 'Country', icon: 'mdi:guitar-acoustic', color: '#F39C12' },
  folk: { label: 'Folk', icon: 'mdi:account-music', color: '#8D6E63' },
  blues: { label: 'Blues', icon: 'mdi:music', color: '#34495E' },
  reggae: { label: 'Reggae', icon: 'mdi:music-note-plus', color: '#E74C3C' },
  punk: { label: 'Punk', icon: 'mdi:guitar-electric', color: '#E67E22' },
  metal: { label: 'Metal', icon: 'mdi:fire', color: '#95A5A6' },
  indie: { label: 'Indie', icon: 'mdi:music-circle', color: '#9B59B6' },
  world: { label: 'World Music', icon: 'mdi:earth', color: '#27AE60' },
  experimental: { label: 'Experimental', icon: 'mdi:test-tube', color: '#16A085' },
  other: { label: 'Other', icon: 'mdi:music-note', color: '#7F8C8D' },
};

export interface StudioCertification {
  id: string;
  organizationId: string;
  title: string;
  type: CertificationType;
  description?: string;
  issuer?: string;
  awardedDate?: Date | Timestamp;
  imageUrl?: string;
  order: number;
  isActive: boolean;
  createdAt?: Date | Timestamp;
}

export interface CreateArtistGalleryRequest {
  organizationId: string;
  isEnabled: boolean;
}

export interface CreateFeaturedArtistRequest {
  organizationId: string;
  galleryId: string;
  name: string;
  photoUrl?: string;
  description?: string;
  projectInfo?: string;
  collaborationDate?: Date;
  websiteUrl?: string;
  socialLinks?: FeaturedArtist['socialLinks'];
  consentStatus?: ConsentStatus;
  order?: number;
  isActive?: boolean;
}

export interface UpdateFeaturedArtistRequest extends Partial<CreateFeaturedArtistRequest> {
  id: string;
}

export interface ArtistGalleryResponse {
  gallery: ArtistGallery;
}

export interface FeaturedArtistsResponse {
  artists: FeaturedArtist[];
  total: number;
}

// ==================== STUDIO CUSTOMIZATION TYPES ====================

export type HeroStyle = 'fullscreen' | 'banner' | 'split' | 'minimal';
export type LogoPosition = 'overlay-left' | 'center' | 'above' | 'side';
export type CTAPosition = 'hero' | 'below' | 'floating' | 'multiple';
export type ColorScheme = 'dark' | 'light' | 'studio' | 'professional' | 'custom';
export type TextSize = 'small' | 'medium' | 'large';
export type GalleryLayout = 'grid' | 'masonry' | 'carousel';
export type MusicPlayerStyle = 'mini' | 'full' | 'sidebar';
export type StudioPersonality = 'professional' | 'creative' | 'premium' | 'community';

export interface StudioLayoutSettings {
  heroStyle: HeroStyle;
  logoPosition: LogoPosition;
  sectionOrder: string[]; // Array of section IDs like ['info', 'gallery', 'music', 'services']
  ctaPosition: CTAPosition;
}

export interface StudioThemeSettings {
  colorScheme: ColorScheme;
  primaryColor?: string; // For custom theme
  accentColor?: string;
  typography: {
    headingFont: string;
    bodyFont: string;
    textSize: TextSize;
  };
}

export interface StudioContentSettings {
  // Section visibility controls
  showReviews: boolean;
  showEquipment: boolean;
  showPricing: boolean;
  showAvailability: boolean;
  showContact: boolean;

  // Custom content
  customWelcomeMessage: string;
  customCTAText: string;
  socialLinks:
    | string[]
    | {
        instagram?: string;
        facebook?: string;
        twitter?: string;
        youtube?: string;
        tiktok?: string;
        website?: string;
        [key: string]: string | undefined;
      };

  // Advanced section controls
  sections: {
    [sectionId: string]: {
      enabled: boolean;
      maxItems?: number;
      displayStyle?: string;
    };
  };
  gallery: {
    layout: GalleryLayout;
    gridSize: 2 | 3 | 4;
    showCaptions: boolean;
  };
  music: {
    playerStyle: MusicPlayerStyle;
    autoplay: boolean;
    showTrackInfo: boolean;
  };
}

export interface StudioCustomization {
  organizationId: string;

  // Layout Configuration
  layout: StudioLayoutSettings;

  // Visual Theme
  theme: StudioThemeSettings;

  // Content Settings
  content: StudioContentSettings;

  // Personality/Branding
  personality: StudioPersonality;

  // Metadata
  isEnabled: boolean;
  presetUsed?: string; // Track which preset was used initially
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
}

// Default customization settings
export const DEFAULT_STUDIO_CUSTOMIZATION: Omit<StudioCustomization, 'organizationId'> = {
  layout: {
    heroStyle: 'fullscreen',
    logoPosition: 'overlay-left',
    sectionOrder: ['info', 'gallery', 'music', 'services', 'certifications', 'artists', 'contact'],
    ctaPosition: 'hero',
  },
  theme: {
    colorScheme: 'studio',
    primaryColor: '#ec4899',
    accentColor: '#f59e0b',
    typography: {
      headingFont: 'Inter',
      bodyFont: 'Inter',
      textSize: 'medium',
    },
  },
  content: {
    // Section visibility controls
    showReviews: true,
    showEquipment: true,
    showPricing: true,
    showAvailability: true,
    showContact: true,

    // Custom content
    customWelcomeMessage: '',
    customCTAText: 'Book Now',
    socialLinks: [],

    // Advanced section controls
    sections: {
      info: { enabled: true },
      gallery: { enabled: true, maxItems: 12 },
      music: { enabled: true, maxItems: 6 },
      services: { enabled: true },
      certifications: { enabled: true, maxItems: 8 },
      artists: { enabled: true, maxItems: 6 },
      contact: { enabled: true },
    },
    gallery: {
      layout: 'grid',
      gridSize: 3,
      showCaptions: true,
    },
    music: {
      playerStyle: 'full',
      autoplay: false,
      showTrackInfo: true,
    },
  },
  personality: 'professional',
  isEnabled: true,
};

// Preset configurations for different studio personalities
export const STUDIO_PERSONALITY_PRESETS: Record<StudioPersonality, Partial<StudioCustomization>> = {
  professional: {
    layout: {
      heroStyle: 'banner',
      logoPosition: 'above',
      sectionOrder: ['info', 'services', 'gallery', 'music', 'certifications', 'contact'],
      ctaPosition: 'below',
    },
    theme: {
      colorScheme: 'professional',
      primaryColor: '#374151',
      accentColor: '#059669',
      typography: {
        headingFont: 'Inter',
        bodyFont: 'Inter',
        textSize: 'medium',
      },
    },
    content: {
      showReviews: true,
      showEquipment: true,
      showPricing: true,
      showAvailability: true,
      showContact: true,
      customWelcomeMessage: 'Welcome to our professional recording studio',
      customCTAText: 'Book Session',
      socialLinks: [],
      sections: {
        info: { enabled: true },
        gallery: { enabled: true, maxItems: 8 },
        music: { enabled: true, maxItems: 4 },
        services: { enabled: true },
        certifications: { enabled: true, maxItems: 6 },
        artists: { enabled: true, maxItems: 4 },
        contact: { enabled: true },
      },
      gallery: {
        layout: 'grid',
        gridSize: 2,
        showCaptions: false,
      },
      music: {
        playerStyle: 'mini',
        autoplay: false,
        showTrackInfo: true,
      },
    },
  },
  creative: {
    layout: {
      heroStyle: 'fullscreen',
      logoPosition: 'center',
      sectionOrder: ['gallery', 'music', 'info', 'artists', 'services', 'contact'],
      ctaPosition: 'floating',
    },
    theme: {
      colorScheme: 'studio',
      primaryColor: '#ec4899',
      accentColor: '#f59e0b',
      typography: {
        headingFont: 'Poppins',
        bodyFont: 'Inter',
        textSize: 'large',
      },
    },
    content: {
      showReviews: true,
      showEquipment: true,
      showPricing: false,
      showAvailability: true,
      showContact: true,
      customWelcomeMessage: 'Unleash your creativity in our inspiring studio space',
      customCTAText: 'Start Creating',
      socialLinks: [],
      sections: {
        info: { enabled: true },
        gallery: { enabled: true, maxItems: 15 },
        music: { enabled: true, maxItems: 8 },
        services: { enabled: true },
        certifications: { enabled: true, maxItems: 6 },
        artists: { enabled: true, maxItems: 8 },
        contact: { enabled: true },
      },
      gallery: {
        layout: 'masonry',
        gridSize: 3,
        showCaptions: true,
      },
      music: {
        playerStyle: 'full',
        autoplay: true,
        showTrackInfo: true,
      },
    },
  },
  premium: {
    layout: {
      heroStyle: 'split',
      logoPosition: 'side',
      sectionOrder: [
        'info',
        'certifications',
        'gallery',
        'music',
        'services',
        'artists',
        'contact',
      ],
      ctaPosition: 'multiple',
    },
    theme: {
      colorScheme: 'dark',
      primaryColor: '#d4af37',
      accentColor: '#c9a96e',
      typography: {
        headingFont: 'Playfair Display',
        bodyFont: 'Inter',
        textSize: 'large',
      },
    },
    content: {
      showReviews: true,
      showEquipment: true,
      showPricing: true,
      showAvailability: true,
      showContact: true,
      customWelcomeMessage: 'Experience world-class recording in our premium facilities',
      customCTAText: 'Reserve Now',
      socialLinks: [],
      sections: {
        info: { enabled: true },
        gallery: { enabled: true, maxItems: 20 },
        music: { enabled: true, maxItems: 10 },
        services: { enabled: true },
        certifications: { enabled: true, maxItems: 10 },
        artists: { enabled: true, maxItems: 6 },
        contact: { enabled: true },
      },
      gallery: {
        layout: 'carousel',
        gridSize: 4,
        showCaptions: true,
      },
      music: {
        playerStyle: 'sidebar',
        autoplay: false,
        showTrackInfo: true,
      },
    },
  },
  community: {
    layout: {
      heroStyle: 'banner',
      logoPosition: 'overlay-left',
      sectionOrder: ['info', 'artists', 'music', 'gallery', 'services', 'contact'],
      ctaPosition: 'hero',
    },
    theme: {
      colorScheme: 'light',
      primaryColor: '#6366f1',
      accentColor: '#06b6d4',
      typography: {
        headingFont: 'Open Sans',
        bodyFont: 'Open Sans',
        textSize: 'medium',
      },
    },
    content: {
      showReviews: true,
      showEquipment: true,
      showPricing: true,
      showAvailability: true,
      showContact: true,
      customWelcomeMessage: 'Join our vibrant music community and make your mark',
      customCTAText: 'Join Us',
      socialLinks: [],
      sections: {
        info: { enabled: true },
        gallery: { enabled: true, maxItems: 12 },
        music: { enabled: true, maxItems: 6 },
        services: { enabled: true },
        certifications: { enabled: true, maxItems: 6 },
        artists: { enabled: true, maxItems: 10 },
        contact: { enabled: true },
      },
      gallery: {
        layout: 'grid',
        gridSize: 3,
        showCaptions: true,
      },
      music: {
        playerStyle: 'full',
        autoplay: false,
        showTrackInfo: true,
      },
    },
  },
};

// Simplified PERSONALITY_PRESETS for easier access in components
export const PERSONALITY_PRESETS: Record<
  StudioPersonality,
  {
    name: string;
    description: string;
    features: string[];
    layout: Partial<StudioLayoutSettings>;
    theme: Partial<StudioThemeSettings>;
    content: Partial<StudioContentSettings>;
  }
> = {
  professional: {
    name: 'Professional',
    description: 'Clean, business-focused design for professional studios',
    features: ['Clean Design', 'Business Focus', 'Minimal Layout'],
    layout: STUDIO_PERSONALITY_PRESETS.professional.layout!,
    theme: STUDIO_PERSONALITY_PRESETS.professional.theme!,
    content: {
      ...STUDIO_PERSONALITY_PRESETS.professional.content!,
      showReviews: true,
      showEquipment: true,
      showPricing: true,
      showAvailability: true,
      showContact: true,
      customWelcomeMessage: 'Welcome to our professional recording studio',
      customCTAText: 'Book Session',
      socialLinks: [],
    },
  },
  creative: {
    name: 'Creative',
    description: 'Artistic, expressive styling for creative studios',
    features: ['Artistic Flair', 'Visual Focus', 'Creative Layout'],
    layout: STUDIO_PERSONALITY_PRESETS.creative.layout!,
    theme: STUDIO_PERSONALITY_PRESETS.creative.theme!,
    content: {
      ...STUDIO_PERSONALITY_PRESETS.creative.content!,
      showReviews: true,
      showEquipment: true,
      showPricing: false,
      showAvailability: true,
      showContact: true,
      customWelcomeMessage: 'Unleash your creativity in our inspiring studio space',
      customCTAText: 'Start Creating',
      socialLinks: [],
    },
  },
  premium: {
    name: 'Premium',
    description: 'Luxury, high-end presentation for premium studios',
    features: ['Luxury Feel', 'High-end Design', 'Premium Experience'],
    layout: STUDIO_PERSONALITY_PRESETS.premium.layout!,
    theme: STUDIO_PERSONALITY_PRESETS.premium.theme!,
    content: {
      ...STUDIO_PERSONALITY_PRESETS.premium.content!,
      showReviews: true,
      showEquipment: true,
      showPricing: true,
      showAvailability: true,
      showContact: true,
      customWelcomeMessage: 'Experience world-class recording in our premium facilities',
      customCTAText: 'Reserve Now',
      socialLinks: [],
    },
  },
  community: {
    name: 'Community',
    description: 'Friendly, welcoming atmosphere for community studios',
    features: ['Friendly Vibe', 'Community Focus', 'Welcoming Design'],
    layout: STUDIO_PERSONALITY_PRESETS.community.layout!,
    theme: STUDIO_PERSONALITY_PRESETS.community.theme!,
    content: {
      ...STUDIO_PERSONALITY_PRESETS.community.content!,
      showReviews: true,
      showEquipment: true,
      showPricing: true,
      showAvailability: true,
      showContact: true,
      customWelcomeMessage: 'Join our vibrant music community and make your mark',
      customCTAText: 'Join Us',
      socialLinks: [],
    },
  },
};

// API request/response types
export interface CreateStudioCustomizationRequest {
  organizationId: string;
  customization: Omit<StudioCustomization, 'organizationId' | 'createdAt' | 'updatedAt'>;
}

export interface UpdateStudioCustomizationRequest {
  organizationId: string;
  customization: Partial<Omit<StudioCustomization, 'organizationId' | 'createdAt' | 'updatedAt'>>;
}

export interface StudioCustomizationResponse {
  customization: StudioCustomization;
}

export interface GetStudioCustomizationResponse {
  customization: StudioCustomization | null;
}
